import { useCallback, useState } from 'react'
import { RuleBuilder } from '../../search'
import { usePdfSearch } from './usePdfSearch'
import PdfSearchResults from './PdfSearchResults'
import './PdfSearch.css'

const SCOPES = [
  { id: 'all', label: 'All' },
  { id: 'page', label: 'This page' },
  { id: 'range', label: 'Range' },
]

// Turn the scope control into the [start, end] sent to search. `undefined` means
// the whole document. A viewer that is already limited to an item's pages passes
// that slice as `bounds`, and every scope stays inside it.
function resolveSearchRange({ scope, currentPage, fromPage, toPage, bounds, numPages }) {
  const min = bounds ? bounds[0] : 1
  const max = bounds ? bounds[1] : numPages || null

  const clamp = (n) => {
    let value = n
    if (value < min) value = min
    if (max != null && value > max) value = max
    return value
  }

  if (scope === 'page') {
    const page = clamp(currentPage || min)
    return { pageRange: [page, page] }
  }

  if (scope === 'range') {
    const from = parseInt(fromPage, 10)
    const to = parseInt(toPage, 10)
    if (!Number.isFinite(from) || !Number.isFinite(to)) {
      return { error: 'Enter a start and end page.' }
    }
    const start = clamp(Math.min(from, to))
    const end = clamp(Math.max(from, to))
    if (start > end) return { error: 'That range is outside this document.' }
    return { pageRange: [start, end] }
  }

  return { pageRange: bounds }
}

const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

// Toolbar button that toggles the search side window.
export function PdfSearchToggle({ open, onClick, disabled }) {
  return (
    <button
      type="button"
      className={'pdfsearch-toggle' + (open ? ' is-active' : '')}
      onClick={onClick}
      disabled={disabled}
      title="Search PDF"
      aria-label="Search PDF"
      aria-pressed={open}
    >
      <IconSearch />
    </button>
  )
}

// A closeable side window that opens to the right of the page (not over it), so
// the reader can keep viewing while searching. Uses the same Find/Exempt rule
// builder as Global Text Search (no save), scoped to the open document. The
// parent renders this only when open, so there is no click-outside behavior.
export function PdfSearchPanel({
  standard,
  pageRange,
  currentPage,
  numPages,
  onClose,
  onOpenPage,
  onRun,
}) {
  const { draft, setDraft, state, run, loadMore } = usePdfSearch(standard, pageRange)
  const [scope, setScope] = useState('all')
  const [fromPage, setFromPage] = useState('')
  const [toPage, setToPage] = useState('')
  const [scopeError, setScopeError] = useState('')

  const bounds = Array.isArray(pageRange) && pageRange.length === 2 ? pageRange : null

  const handleRun = useCallback(async () => {
    const resolved = resolveSearchRange({
      scope,
      currentPage,
      fromPage,
      toPage,
      bounds,
      numPages,
    })
    if (resolved.error) {
      setScopeError(resolved.error)
      return
    }
    setScopeError('')
    onRun?.(draft.findRules, resolved.pageRange || null)
    await run({ pageRange: resolved.pageRange })
  }, [bounds, currentPage, draft.findRules, fromPage, numPages, onRun, run, scope, toPage])

  return (
    <aside className="pdfsearch-panel">
      <div className="pdfsearch-header">
        <span className="pdfsearch-title">Search this document</span>
        <button type="button" className="pdfsearch-close" onClick={onClose} aria-label="Close search">
          ×
        </button>
      </div>
      <div className="pdfsearch-scope">
        <span className="pdfsearch-scope-label">Pages</span>
        <div className="ct-toggle" role="group" aria-label="Pages to search">
          {SCOPES.map((option) => (
            <button
              key={option.id}
              type="button"
              className={'ct-toggle-btn' + (scope === option.id ? ' is-active' : '')}
              aria-pressed={scope === option.id}
              onClick={() => {
                setScope(option.id)
                setScopeError('')
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        {scope === 'range' ? (
          <div className="pdfsearch-range">
            <label className="pdfsearch-range-field">
              From
              <input
                className="ct-input pdfsearch-range-input"
                type="number"
                min={bounds ? bounds[0] : 1}
                max={bounds ? bounds[1] : numPages || undefined}
                inputMode="numeric"
                value={fromPage}
                onChange={(event) => setFromPage(event.target.value)}
              />
            </label>
            <label className="pdfsearch-range-field">
              To
              <input
                className="ct-input pdfsearch-range-input"
                type="number"
                min={bounds ? bounds[0] : 1}
                max={bounds ? bounds[1] : numPages || undefined}
                inputMode="numeric"
                value={toPage}
                onChange={(event) => setToPage(event.target.value)}
              />
            </label>
          </div>
        ) : null}
        {scopeError ? <p className="pdfsearch-scope-error">{scopeError}</p> : null}
      </div>
      <div className="pdfsearch-builder">
        <RuleBuilder
          draft={draft}
          onChange={setDraft}
          onRun={handleRun}
          running={state.status === 'loading'}
        />
      </div>
      <div className="pdfsearch-results-scroll">
        <PdfSearchResults state={state} onOpenPage={onOpenPage} onLoadMore={loadMore} />
      </div>
    </aside>
  )
}
